import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./Toast.module.css";
import { ToastContext, type ToastApi, type ToastKind } from "./toastContext";
import { takePendingToast } from "./pendingToast";

interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
}

const lifetimeMs = 8000;
const anchorSelector = "[data-toast-anchor]";

/** The lowest edge of the visible top chrome (header, and the admin menu bar when present), or 0. */
function chromeBottom(): number {
  return Math.max(
    0,
    ...Array.from(document.querySelectorAll(anchorSelector), (element) =>
      Math.max(0, element.getBoundingClientRect().bottom),
    ),
  );
}

/**
 * Owns the stack of toasts and renders them in a polite live region at the bottom of the viewport.
 * Each toast dismisses itself after {@link lifetimeMs} and can be closed by hand.
 */
export function ToastProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(0);
  const toastTimeouts = useRef(new Map<number, ReturnType<typeof window.setTimeout>>());
  const [top, setTop] = useState(0);

  useEffect(() => {
    const update = (): void => setTop(chromeBottom());
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [toasts.length]);

  const dismiss = useCallback((id: number): void => {
    const timeout = toastTimeouts.current.get(id);
    if (timeout !== undefined) {
      window.clearTimeout(timeout);
      toastTimeouts.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  useEffect(() => {
    const timeouts = toastTimeouts.current;
    return () => {
      for (const timeout of timeouts.values()) {
        window.clearTimeout(timeout);
      }
      timeouts.clear();
    };
  }, []);

  const show = useCallback(
    (message: string, kind: ToastKind = "info"): void => {
      nextId.current += 1;
      const id = nextId.current;
      setToasts((current) => [...current, { id, message, kind }]);
      const timeout = window.setTimeout(() => {
        toastTimeouts.current.delete(id);
        dismiss(id);
      }, lifetimeMs);
      toastTimeouts.current.set(id, timeout);
    },
    [dismiss],
  );

  // A confirmation queued before a full reload (see pendingToast.ts) is shown once the portal is back.
  useEffect(() => {
    const pending = takePendingToast();
    if (pending) {
      show(pending.message, pending.kind);
    }
  }, [show]);

  const api = useMemo<ToastApi>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className={styles.region}
        style={{ top: top + 12 }}
        role="status"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`${styles.toast} ${styles[toast.kind]}`}
          >
            <span className={styles.message}>{toast.message}</span>
            <button
              type="button"
              className={styles.close}
              onClick={() => dismiss(toast.id)}
              aria-label={t("toast.dismiss", "Dismiss notification")}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
